<template>
  <div class="top-nav">
    <div class="nav-container">
      <!-- Logo 区域 -->
      <div
        class="nav-logo"
        @click="$router.push('/')"
      >
        <img
          src="/image/logo2.png"
          alt="SKILLITH-EDU"
          class="logo-image"
          width="90"
          height="44"
          fetchpriority="high"
          decoding="async"
        >
      </div>
      
      <!-- 导航菜单（用户端与管理端均显示，便于从管理页快速返回） -->
      <nav class="nav-menu">
        <router-link
          v-for="item in visibleNavItems"
          :key="item.path"
          :to="item.path"
          class="nav-item"
          :class="{ active: isRouteActive(item.path), 'nav-item--disabled': isShopLocked && item.path === '/shop' }"
          @click.prevent="handleNavClick(item.path)"
        >
          <el-icon><component :is="item.icon" /></el-icon>
          <span>{{ item.label }}</span>
        </router-link>

        <!-- 自适应“更多”菜单：在宽度不够时折叠多余入口 -->
        <el-dropdown
          v-if="hasMoreItems"
          trigger="hover"
          class="nav-more"
        >
          <div
            class="nav-item more-item"
            :class="{ active: isMoreActive }"
          >
            <el-icon class="more-icon">
              <MoreFilled />
            </el-icon>
            <span>更多</span>
          </div>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item
                v-for="item in moreNavItems"
                :key="item.path"
                :disabled="isShopLocked && item.path === '/shop'"
                :title="isShopLocked && item.path === '/shop' ? '达到白银段位后可进入' : undefined"
                @click="handleMoreItemClick(item)"
              >
                <el-icon class="more-icon"><component :is="item.icon" /></el-icon>
                <span>{{ item.label }}</span>
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </nav>
      
      <!-- 右侧用户菜单 -->
      <div class="nav-right">
        <!-- 赞助按钮（使用 btn-donate 样式），仅在导航未折叠且空间充足时显示，避免与“更多”重合 -->
        <router-link
          v-if="showDonateButton"
          to="/sponsor"
          class="btn-donate"
          :class="{ active: $route.path === '/sponsor' }"
        >
          赞助一下
        </router-link>
        <!-- 主题切换按钮：兑换「界面主题切换」后始终固定在右上角 -->
        <div
          v-if="themeStore.unlocked"
          class="nav-theme-switch-wrapper"
        >
          <ThemeSwitch class="nav-theme-switch" />
        </div>
        <!-- 通知中心 -->
        <div
          v-if="authStore.user"
          class="notification-bell"
          @click="router.push('/me/notifications')"
        >
          <el-badge
            :value="unreadCount"
            :hidden="unreadCount === 0"
            :max="99"
          >
            <el-icon :size="20">
              <Bell />
            </el-icon>
          </el-badge>
        </div>
        
        <el-dropdown
          trigger="click"
          teleported
          popper-class="top-nav-user-dropdown"
          @command="handleCommand"
          v-if="authStore.user"
        >
          <div class="user-menu">
            <AvatarWithFrame
              :avatar-url="displayAvatarUrl"
              :frame-config="avatarFrameStore.getFrameConfig()"
              :size="32"
              :center-text="authStore.user?.gradientAvatarCenterText || ''"
              center-text-size="18px"
            />
            <div class="user-info">
              <span class="username">{{ authStore.user.username }}</span>
              <UserTitle
                v-if="avatarFrameStore.getTitleConfig()"
                :title-config="avatarFrameStore.getTitleConfig()"
                size="compact"
                class="user-title-display"
              />
            </div>
            <el-icon><ArrowDown /></el-icon>
          </div>
          <template #dropdown>
            <el-dropdown-menu>
              <!-- 与主导航保持一致的全局入口 -->
              <el-dropdown-item command="challenges">
                <el-icon><Trophy /></el-icon>
                <span>挑战训练场</span>
              </el-dropdown-item>
              <el-dropdown-item command="leaderboard">
                <el-icon><DataLine /></el-icon>
                <span>排行榜</span>
              </el-dropdown-item>
              <el-dropdown-item command="shop">
                <el-icon><ShoppingBag /></el-icon>
                <span>积分商城</span>
              </el-dropdown-item>
              <el-dropdown-item command="kb">
                <el-icon><Document /></el-icon>
                <span>知识库</span>
              </el-dropdown-item>
              <el-dropdown-item command="friends">
                <el-icon><ChatLineRound /></el-icon>
                <span>好友聊天</span>
              </el-dropdown-item>
              <el-dropdown-item
                command="profile"
                divided
              >
                <el-icon><User /></el-icon>
                <span>个人主页</span>
              </el-dropdown-item>
              <el-dropdown-item command="achievements">
                <el-icon><Medal /></el-icon>
                <span>我的成就</span>
              </el-dropdown-item>
              <el-dropdown-item
                command="feedback"
              >
                <el-icon><ChatDotRound /></el-icon>
                <span>意见反馈</span>
              </el-dropdown-item>
              <el-dropdown-item
                v-if="showAdminMenu"
                command="admin"
                divided
              >
                <el-icon><Setting /></el-icon>
                <span>管理后台</span>
              </el-dropdown-item>
              <el-dropdown-item
                command="logout"
                divided
              >
                <el-icon><SwitchButton /></el-icon>
                <span>退出登录</span>
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
        
        <el-button
          v-else
          type="primary"
          @click="$router.push('/login')"
        >
          登录
        </el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, watch, onMounted, onBeforeUnmount, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notification'
import { useAvatarFrameStore } from '@/stores/avatarFrame'
import { useRankStore } from '@/stores/rank'
import { useThemeStore } from '@/stores/theme'
import { getImageUrl } from '@/utils/imageUrl'
import AvatarWithFrame from '@/components/ui/AvatarWithFrame.vue'
import UserTitle from '@/components/ui/UserTitle.vue'
import ThemeSwitch from '@/components/ui/ThemeSwitch.vue'
import {
  User,
  Medal,
  ChatDotRound,
  Trophy,
  DataLine,
  ShoppingBag,
  Document,
  ChatLineRound,
  Setting,
  SwitchButton,
  ArrowDown,
  Bell,
  MoreFilled
} from '@element-plus/icons-vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const notificationStore = useNotificationStore()
const avatarFrameStore = useAvatarFrameStore()
const rankStore = useRankStore()
const themeStore = useThemeStore()

const isAdminRoute = computed(() => route.path.startsWith('/admin'))
const showAdminMenu = computed(() => {
  return authStore.isAdmin() || authStore.isSuperAdmin()
})
const unreadCount = computed(() => notificationStore.unreadCount)

// 积分商城是否锁定（未达白银）：仅在段位已加载且未达白银时为 true，避免未加载时误锁导致布局异常
const isShopLocked = computed(() => {
  if (!rankStore.loaded) return false
  return !rankStore.isAtLeastSilver
})

const handleMoreItemClick = (item) => {
  if (item.path === '/shop' && isShopLocked.value) {
    ElMessage.warning('达到白银段位后可进入积分商城')
    return
  }
  router.push(item.path)
}

// 主导航点击：用 .prevent 拦截默认跳转，由此处统一决定是否跳转（积分商城需白银段位）
const handleNavClick = (path) => {
  if (path === '/shop') {
    if (rankStore.loaded && !rankStore.isAtLeastSilver) {
      ElMessage.warning('达到白银段位后可进入积分商城')
      return
    }
  }
  router.push(path)
}

// 展示用头像：优先使用普通头像 URL，其次才是渐变标识；
// 如果 avatarUrl 显式为空字符串，则强制使用默认头像（忽略渐变）
const displayAvatarUrl = computed(() => {
  const u = authStore.user
  if (!u) return ''
  // 显式为空：表示用户选择了“默认头像”，此时不再展示任何渐变装扮
  if (u.avatarUrl === '') {
    return ''
  }
  // 如果 avatarUrl 是正常图片（非 GRADIENT_ 开头），优先使用它
  if (u.avatarUrl && !u.avatarUrl.startsWith('GRADIENT_')) {
    return u.avatarUrl
  }
  // 否则再看渐变标识或保底的 avatarUrl
  return u.avatarGradientType || u.avatarUrl || ''
})

const getAvatarUrl = computed(() => {
  return getImageUrl(authStore.user?.avatarUrl)
})

// 视口宽度，用于决定显示多少个一级导航，剩余的放入“更多”菜单
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1920)

const updateWindowWidth = () => {
  if (typeof window === 'undefined') return
  windowWidth.value = window.innerWidth || document.documentElement.clientWidth || 1920
}

watch(() => authStore.user, (user) => {
  if (user) {
    notificationStore.startPolling()
    avatarFrameStore.loadAll()
    rankStore.ensureLoaded()
  } else {
    notificationStore.stopPolling()
    avatarFrameStore.activeFrame = null
    avatarFrameStore.activeTitle = null
  }
}, { immediate: true })

onMounted(() => {
  if (authStore.user) {
    avatarFrameStore.loadAll()
    rankStore.ensureLoaded()
  }
  updateWindowWidth()
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', updateWindowWidth)
  }
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateWindowWidth)
  }
})

const allNavItems = [
  { path: '/challenges', label: '挑战训练场', icon: 'Trophy' },
  { path: '/leaderboard', label: '排行榜', icon: 'DataLine' },
  { path: '/shop', label: '积分商城', icon: 'ShoppingBag' },
  { path: '/kb', label: '知识库', icon: 'Document' },
  { path: '/me/friends', label: '好友聊天', icon: 'ChatLineRound' },
  { path: '/me/profile', label: '个人主页', icon: 'User' },
  { path: '/me/achievements', label: '我的成就', icon: 'Medal' }
]

// 根据当前窗口宽度决定直接展示多少个一级导航，其余的折叠到“更多”里
const visibleNavItems = computed(() => {
  const w = windowWidth.value
  if (w >= 1500) {
    // 超宽屏：全部展示
    return allNavItems
  }
  if (w >= 1320) {
    // 宽屏：展示前 6 个，其余进“更多”
    return allNavItems.slice(0, 6)
  }
  if (w >= 1180) {
    // 中等宽度：展示前 4 个，其余进“更多”
    return allNavItems.slice(0, 4)
  }
  // 更窄时：只展示前 3 个入口
  return allNavItems.slice(0, 3)
})

const moreNavItems = computed(() => {
  const visible = visibleNavItems.value
  if (visible.length >= allNavItems.length) return []
  const visibleSet = new Set(visible.map(i => i.path))
  return allNavItems.filter(i => !visibleSet.has(i.path))
})

const hasMoreItems = computed(() => moreNavItems.value.length > 0)

// 仅在窗口特别窄、完全放不下时才隐藏赞助按钮
const showDonateButton = computed(() => {
  const w = windowWidth.value
  // 非常窄的小屏（导航本身都要被压缩）再考虑隐藏
  if (w <= 1120) return false
  // 其余绝大部分宽度下始终展示“赞助一下”
  return true
})

const isRouteActive = (path) => {
  return route.path.startsWith(path)
}

const isMoreActive = computed(() => {
  return moreNavItems.value.some(item => isRouteActive(item.path))
})

const handleCommand = (command) => {
  switch (command) {
    case 'challenges':
      router.push('/challenges')
      break
    case 'leaderboard':
      router.push('/leaderboard')
      break
    case 'shop':
      if (rankStore.loaded && !rankStore.isAtLeastSilver) {
        ElMessage.warning('达到白银段位后可进入积分商城')
        return
      }
      router.push('/shop')
      break
    case 'kb':
      router.push('/kb')
      break
    case 'profile':
      router.push('/me/profile')
      break
    case 'achievements':
      router.push('/me/achievements')
      break
    case 'friends':
      router.push('/me/friends')
      break
    case 'feedback':
      router.push('/me/feedback')
      break
    case 'admin':
      router.push('/admin/dashboard')
      break
    case 'logout':
      authStore.logout()
      router.push('/login')
      break
  }
}
</script>

<style scoped lang="scss">
.top-nav {
  background: var(--color-bg-card);
  border-bottom: 1px solid var(--color-border);
  box-shadow: var(--shadow-sm);
  position: sticky !important;
  top: 0 !important;
  z-index: 1000 !important;
  height: 72px !important;
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  isolation: isolate;
  
  // 当有 Profile 背景时，深色主题下使用半透明深色+模糊，保持可读
  &.with-background {
    background: rgba(21, 29, 46, 0.92);
    backdrop-filter: blur(10px);
    z-index: 1000 !important;
  }
}

/* 亮色主题：使用 sticky 与深色一致，保证文档流正常、页面可垂直滚动 */
:global(body[data-theme='light']) .top-nav {
  position: sticky !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  width: 100% !important;
  margin: 0 !important;
  padding-top: 0 !important;
}

// 亮色主题：GitHub 风格 — 纯白导航 + 细底边，无渐变
body[data-theme='light'] .top-nav,
body[data-theme='light'] .top-nav.with-background {
  background: #ffffff;
  border-bottom: 1px solid #d0d7de;
  backdrop-filter: none;
  box-shadow: 0 1px 3px rgba(31, 35, 40, 0.08);
}

/* 亮色主题：隐藏导航任何 ::after（含双圆渐变），避免背后出现椭圆形大块 */
body[data-theme='light'] .top-nav::after {
  display: none !important;
}

.nav-container {
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 var(--spacing-xl);
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--spacing-lg);
}

.nav-logo {
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: opacity var(--transition-fast) var(--ease-out);
  height: 72px;
  flex-shrink: 0;
  
  &:hover {
    opacity: 0.85;
  }
  
  .logo-image {
    height: 44px;
    width: auto;
    object-fit: contain;
    max-width: none;
    user-select: none;
    pointer-events: none;
  }
}

/* 亮色主题下：Logo 使用深色版本，通过滤镜反转为黑色系 */
:global(body[data-theme='light'] .top-nav .logo-image) {
  filter: invert(1) grayscale(1);
}

.nav-menu {
  display: flex;
  gap: var(--spacing-sm);
  flex: 1;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: nowrap;
  min-width: 0;
  
  @media (max-width: 1200px) {
    gap: var(--spacing-xs);
  }
  
  // 小屏或高缩放时，隐藏主导航，仅保留 Logo + 右侧用户区，避免与按钮/头像重叠
  @media (max-width: 1024px) {
    display: none;
  }
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  transition: all var(--transition-fast) var(--ease-out);
  
  .el-icon {
    font-size: 18px;
  }
  
  &:hover {
    color: var(--color-primary);
    background: var(--color-primary-50);
  }
  
  &.active {
    color: var(--color-primary);
    background: var(--color-primary-50);
    font-weight: 600;
  }
  
  &.nav-item--disabled {
    cursor: not-allowed;
    opacity: 0.7;
    &:hover {
      color: var(--color-text-secondary);
      background: transparent;
    }
  }
}

.nav-more {
  .more-item {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .more-icon {
    margin-right: 4px;
  }
}

.nav-right {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  flex-shrink: 0;
  margin-left: auto;
}

.nav-theme-switch-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-theme-switch {
  --toggle-size: 20px;
}

.btn-donate {
  --btn-bg-1: #38bdf8;
  --btn-bg-2: #3b82f6;
  --btn-bg-color: #f1f5f9;
  --radii: 0.5em;
  cursor: pointer;
  padding: 0.7em 1.4em;
  min-width: 120px;
  min-height: 40px;
  font-size: 0.9rem;
  font-weight: 500;
  transition: 0.8s;
  background-size: 280% auto;
  background-image: linear-gradient(
    325deg,
    var(--btn-bg-2) 0%,
    var(--btn-bg-1) 55%,
    var(--btn-bg-2) 90%
  );
  border: 1px solid rgba(59, 130, 246, 0.4);
  border-radius: var(--radii);
  color: var(--btn-bg-color);
  box-shadow: var(--shadow-sm), 0 0 14px rgba(59, 130, 246, 0.2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  white-space: nowrap;
}

// （保留样式占位，可按需扩展）「赞助一下」按钮是否显示由 showDonateButton 统一控制

.btn-donate:hover {
  background-position: right top;
}

.btn-donate:is(:focus, :focus-visible, :active) {
  outline: none;
  box-shadow: 0 0 0 2px var(--color-primary);
}

.notification-bell {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast) var(--ease-out);
  color: var(--color-text-secondary);
  position: relative;
  
  &:hover {
    background: var(--color-bg-hover);
    color: var(--color-primary);
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
}

.user-menu {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px 4px 4px;
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: all var(--transition-fast) var(--ease-out);
  border: 1px solid transparent;
  
  &:hover {
    background: var(--color-bg-hover);
    border-color: var(--color-border);
  }
  
  .user-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    min-width: 0;
    
      .username {
        font-size: 14px;
        font-weight: 500;
        color: var(--color-text-primary);
        max-width: 120px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        
        @media (max-width: 1024px) {
          max-width: 80px;
        }
        
        @media (max-width: 768px) {
          display: none;
        }
      }
      
      .user-title-display {
        font-size: 11px;
        max-width: 120px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  
  .el-icon {
    color: var(--color-text-secondary);
    font-size: 14px;
    transition: transform var(--transition-fast) var(--ease-out);
  }
  
  &:hover .el-icon {
    transform: translateY(1px);
  }
}
</style>
